<template>
  <div class="d-flex">
    <b-container fluid class="p-0">
      <b-row no-gutters>
        <b-col cols="3" class="pr-3">
          <slot name="ruleID" :rule="rule" :options="ruleIDOptions">
            <v-select
              v-model="rule.id"
              :items="ruleIDOptions"
              item-text="title"
              item-value="name"
              dense
              outlined
            />
          </slot>
        </b-col>
        <b-col cols="4" class="pr-3">
          <slot name="ruleOperator" :rule="rule" :options="ruleOperatorOptions">
            <v-select
              v-model="rule.operator"
              :label="$t('condition')"
              :items="ruleOperatorOptions"
              item-text="title"
              item-value="name"
              dense
              outlined
            />
          </slot>
        </b-col>

        <b-col cols="5" class="pr-3" v-if="ruleParams">
          <slot
            name="dropdown_multiple"
            :rule="rule"
            :multiple="multiple"
            :options="ruleParams.options"
            v-if="ruleParams.type === 'dropdown_multi_search'"
          >
            <v-combobox
              v-model="rule.value"
              :label="ruleParams.title"
              :items="ruleParams.options"
              item-text="text"
              return-object
              dense
              outlined
              clearable
              :multiple="multiple"
              small-chips
              deletable-chips
              hide-no-data
              hide-selected
              :placeholder="$t('enter_at_least_2_characters')"
              :loading="isLoading"
              :search-input.sync="search"
              :error-messages="ruleValueErrors"
              @blur="$v.rule.value.$touch()"
            />
          </slot>

          <slot
            name="number"
            :rule="rule"
            v-else-if="ruleParams.type === 'number'"
          >
            <v-text-field
              v-model="rule.value"
              :label="ruleParams.title"
              dense
              outlined
              clearable
              type="number"
              hide-details
              :error-messages="ruleValueErrors"
              @blur="$v.rule.value.$touch()"
            />
          </slot>

          <slot
            name="text"
            :rule="rule"
            v-else-if="
              ruleParams.type === 'text' && rule.operator !== 'is_empty'
            "
          >
            <v-text-field
              v-model="rule.value"
              :label="ruleParams.title"
              dense
              outlined
              clearable
              hide-details
              :error-messages="ruleValueErrors"
              @blur="$v.rule.value.$touch()"
            />
          </slot>

          <slot
            name="dropdown"
            :rule="rule"
            v-else-if="ruleParams.type === 'dropdown'"
          >
            <v-autocomplete
              v-model="rule.value"
              :label="ruleParams.title"
              :items="ruleParams.values"
              item-text="text"
              item-value="index"
              return-object
              dense
              outlined
              clearable
              hide-details
              :error-messages="ruleValueErrors"
              @blur="$v.rule.value.$touch()"
            />
          </slot>

          <slot
            name="dropdown_multi_select"
            :rule="rule"
            v-else-if="ruleParams.type === 'dropdown_multi_select'"
          >
            <v-autocomplete
              v-model="rule.value"
              :label="ruleParams.title"
              :items="ruleParams.values"
              item-text="text"
              item-value="index"
              small-chips
              deletable-chips
              return-object
              multiple
              dense
              outlined
              hide-details
              clearable
              :error-messages="ruleValueErrors"
              @blur="$v.rule.value.$touch()"
            >
              <template v-slot:prepend-item>
                <v-list-item ripple @mousedown.prevent @click="toggle">
                  <v-list-item-action>
                    <v-icon
                      :color="rule.value.length > 0 ? 'purple darken-2' : ''"
                    >
                      {{
                        rule.value.length === 0
                          ? "mdi-checkbox-blank-outline"
                          : rule.value.length === ruleParams.values.length
                          ? "mdi-close-box"
                          : "mdi-minus-box"
                      }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title> Select All </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
              <template v-slot:selection="{ attrs, item, parent, selected }">
                <v-chip
                  v-if="item === Object(item)"
                  v-bind="attrs"
                  :input-value="selected"
                  label
                  small
                >
                  <span class="pr-2">
                    {{ item.text }}
                  </span>
                  <v-icon small @click="parent.selectItem(item)">
                    $delete
                  </v-icon>
                </v-chip>
              </template>
            </v-autocomplete>
          </slot>
          <slot
            name="text_field"
            :rule="rule"
            v-else-if="ruleParams.type === 'text_field'"
          >
            <v-text-field
              v-model="rule.value"
              :label="ruleParams.title"
              persistent-hint
              small-chips
              deletable-chips
              dense
              outlined
              :error-messages="ruleValueErrors"
              @blur="onBlurTextField"
              @touch="$v.rule.value.$touch()"
              hint="Values should be comma (,) seperated."
            >
            </v-text-field>
          </slot>
          <slot
            name="dropdown"
            :rule="rule"
            v-else-if="ruleParams.type === 'time_range'"
          >
            <div class="row ma-0 pa-0">
              <div class="col-6 ma-0 pa-0 pr-3">
                <v-select
                  v-model="rangeValue[0]"
                  item-text="text"
                  item-value="index"
                  :items="[
                    // { index: 0, text: ' 0 am' },
                    ...Array.from({ length: 24 }, (_, i) => ({
                      index: i,
                      text: `${i % 12 != 0 ? i % 12 : i != 0 ? 12 : 0} ${
                        i < 12 ? 'am' : 'pm'
                      }`,
                    })),
                  ]"
                  :placeholder="$t('start_time')"
                  dense
                  outlined
                  clearable
                  :error-messages="ruleValueErrors"
                  @blur="$v.rule.value.$touch()"
                />
              </div>
              <div class="col-6 ma-0 pa-0 pr-3">
                <v-select
                  v-model="rangeValue[1]"
                  :items="timeRangeEndValue"
                  :placeholder="$t('end_time')"
                  item-text="text"
                  item-value="index"
                  dense
                  outlined
                  clearable
                  :error-messages="ruleValueErrors"
                  @blur="$v.rule.value.$touch()"
                />
              </div>
            </div>
          </slot>
        </b-col>
      </b-row>
    </b-container>
    <div class="max-h-65">
      <button
        class="h-40px w-40px btn btn-sm btn-icon btn-light-info btn-hover-light"
        @click="deleteRule"
      >
        <span class="svg-icon">
          <v-icon size="18">mdi-delete</v-icon>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { getToken } from "@/core/services/jwt.service";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import { SET_NOT_VALID } from "@/core/services/store/shipmentCreationRules.module";

export default {
  name: "Rule",
  mixins: [validationMixin],
  validations: {
    rule: {
      value: { required },
    },
  },
  props: {
    rule: {
      type: Object,
      required: true,
    },
    options: {
      type: Array,
    },
    userId: {
      required: false,
    },
  },
  mounted() {
    this.$v.rule.value.$touch();
    if (
      this.ruleParams.name === "sender_city" ||
      this.ruleParams.name === "sender_country" ||
      this.ruleParams.name === "sender_area" ||
      this.ruleParams.name === "receiver_city" ||
      this.ruleParams.name === "receiver_country" ||
      this.ruleParams.name === "receiver_area"
    ) {
      // console.log('rule',this.rule)
      if (this.rule.value == "") {
        this.ruleParams.options = [];
      } else {
        this.ruleParams.options = this.rule.value;
      }

      // this.search = this.rule.value[0].text;
      this.$store.commit(SET_NOT_VALID, false);
    }

    if (this.ruleParams.type == "time_range") {
      this.rangeValue = Array.from(
        String(this.rule.value).match(/\d+/g),
        Number
      );
    }
  },
  data: () => ({
    search: null,
    isLoading: false,
    doneTypingInterval: 1500,
    typingTimer: null,
    rangeValue: [0, 1],
  }),
  computed: {
    timeRangeEndValue() {
      return Array.from({ length: 24 }, (_, i) => ({
        index: i,
        text: `${i % 12 != 0 ? i % 12 : i != 0 ? 12 : 0} ${
          i < 12 ? "am" : "pm"
        }`,
      })).slice(this.rangeValue[0] + 1);
    },
    ruleParams() {
      let res;
      res = this.options.find((option) => option.name === this.rule.id);
      // console.log("res", res);
      return res;
    },
    ruleIDOptions() {
      const options = this.options;
      return options.map(function (option) {
        return {
          name: option.name,
          title: option.title,
        };
      });
    },
    currentRuleID: function () {
      return this.rule.id;
    },
    currentRuleOperator: function () {
      return this.rule.operator;
    },
    multiple: function () {
      return this.rule.operator.includes("in");
    },
    ruleOperatorOptions: function () {
      if (this.ruleParams) return this.ruleParams.operators;
      else return [];
    },
    ruleValueErrors: function () {
      return this.handleFormValidation("value");
    },
  },
  methods: {
    toggle() {
      this.$nextTick(() => {
        if (this.rule.value.length === this.ruleParams.values.length) {
          this.rule.value = [];
        } else {
          this.rule.value = this.ruleParams.values.map((value) => value.index);
        }
      });
    },
    onBlurTextField() {
      this.$v.rule.value.$touch();
      const values = this.rule.value;
      this.rule.value = values.split(",");
    },
    handleFormValidation(fieldName) {
      const errors = [];
      if (!this.$v.rule[fieldName].$dirty) return errors;
      if ("required" in this.$v.rule[fieldName]) {
        !this.$v.rule[fieldName].required &&
          errors.push(this.$t("validation_this_field_is_required"));
      }
      return errors;
    },
    resetRuleValue: function () {
      if (this.multiple && this.ruleParams.type === "dropdown_multiple") {
        this.rule.value = [];
      } else {
        this.rule.value = "";
      }
      if (
        this.ruleParams.hasOwnProperty("options") &&
        this.ruleParams.options.length
      ) {
        this.ruleParams.options = [];
      }
    },
    deleteRule: function () {
      const self = this;
      const parentRules = self.$parent.currentQuery.rules;
      parentRules.splice(parentRules.indexOf(self.rule), 1);
    },
    async getData(value) {
      let requestBody = { q: value };
      if (this.userId && this.ruleParams.name === "sku") {
        if (Array.isArray(this.userId)) {
          requestBody.clients = this.userId.map((item) => item.index);
        } else requestBody.client = this.userId;
      }

      await fetch(
        `${process.env.VUE_APP_BASE_URL}${this.ruleParams.search_url}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(requestBody),
        }
      )
        .then((res) => res.json())
        .then((res) => {
          if (this.ruleParams.name === "sku" && res.skus) {
            // Keep previous options and add new ones, avoiding duplicates by index
            const prevOptions = this.ruleParams.options
              ? this.ruleParams.options
              : [];
            const newOptions = res.skus.map((item) => ({
              index: item.skuId,
              text: item.systemCode,
            }));
            // Merge and deduplicate by index
            const mergedOptions = [
              ...prevOptions,
              ...newOptions.filter(
                (newItem) =>
                  !prevOptions.some(
                    (prevItem) => prevItem.index === newItem.index
                  )
              ),
            ];
            this.ruleParams.options = mergedOptions;
          } else if (
            this.ruleParams.name === "sender_country" &&
            res.countries
          ) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.countries,
              ]),
            ];
          } else if (this.ruleParams.name === "sender_city" && res.cities) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.cities,
              ]),
            ];
          } else if (this.ruleParams.name === "sender_area" && res.areas) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.areas,
              ]),
            ];
          } else if (
            this.ruleParams.name === "receiver_country" &&
            res.countries
          ) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.countries,
              ]),
            ];
          } else if (this.ruleParams.name === "receiver_city" && res.cities) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.cities,
              ]),
            ];
          } else if (this.ruleParams.name === "receiver_area" && res.areas) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.areas,
              ]),
            ];
          }
        })
        .finally(() => (this.isLoading = false));
    },
  },
  watch: {
    rangeValue: {
      handler() {
        if (this.ruleParams.type === "time_range") {
          this.rule.value = this.rangeValue.join("-");
        }
      },
      deep: true,
    },
    currentRuleID: function () {
      this.resetRuleValue();
    },
    currentRuleOperator: function (to, from) {
      if (to.includes("in") !== from.includes("in")) {
        this.resetRuleValue();
      }
      return false;
    },
    search(val) {
      clearTimeout(this.typingTimer);
      if (!val || val === "") {
        return;
      }
      this.typingTimer = setTimeout(() => {
        this.isLoading = true;
        this.getData(val.trim());
      }, this.doneTypingInterval);
    },
    rule: {
      handler(newValue) {
        if (Array.isArray(newValue.value)) {
          if (newValue.value.length) this.$store.commit(SET_NOT_VALID, false);
          else this.$store.commit(SET_NOT_VALID, true);
        } else {
          if (newValue.value) this.$store.commit(SET_NOT_VALID, false);
          else {
            if (
              newValue.id === "consignee_document_number" &&
              newValue.operator === "is_empty"
            )
              this.$store.commit(SET_NOT_VALID, false);
            else this.$store.commit(SET_NOT_VALID, true);
          }
        }
        if (newValue.id === "sku" && newValue?.value) {
          if (Array.isArray(newValue.value)) {
            newValue.value.forEach((val) => {
              this.getData(val.text.trim());
            });
          } else this.getData(newValue.value.text.trim());
        }
        // this.$nextTick(() => {
        //   this.updateComponent();
        // });
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
